import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { EmailCaptureConfig } from '@/types/vsleads/emailCaptureTypes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Settings, Loader2 } from 'lucide-react';
import { videoService } from '@/services/videoService';
import { VideoFolderPicker } from '@/components/shared/VideoFolderPicker';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface ContentTabProps {
  form: UseFormReturn<EmailCaptureConfig>;
  isEditMode?: boolean;
}

export function ContentTab({ form, isEditMode = false }: ContentTabProps) {
  useLanguage();
  const { user } = useAuth();
  const videoId = form.watch('videoId');
  const isActive = form.watch('isActive');
  const [videos, setVideos] = useState<Array<{ id: string; title: string }>>([]);
  const [isLoadingVideos, setIsLoadingVideos] = useState(false);

  const userId = user?.id || user?.user_id;

  useEffect(() => {
    if (userId && !isEditMode) {
      loadVideos();
    }
  }, [userId, isEditMode]);

  const loadVideos = async () => {
    if (!userId) return;

    setIsLoadingVideos(true);
    try {
      const result = await videoService.getDashboardVideos(userId);

      if (Array.isArray(result)) {
        setVideos(result.map(video => ({
          id: video.id,
          title: video.title
        })));
      } else {
        setVideos([]);
      }
    } catch (error) {
      console.error('Erro ao carregar vídeos:', error);
      toast.error(translateInterfaceText("Erro ao carregar vídeos"));
      setVideos([]);
    } finally {
      setIsLoadingVideos(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Card 1: ESTRATÉGIA DE CAPTURA - PRIMEIRO (seguindo padrão dos Controles) */}
      <div className="space-y-4">
        <div className="bg-[#F7F8FE] rounded-xl p-4 space-y-3 border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
          {/* Toggle com ícone dinâmico */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings
                className={`w-5 h-5 transition-colors duration-200 ${
                  isActive ? 'text-[#2D6CF6]' : 'text-[#94A3B8]'
                }`}
                strokeWidth={2}
              />
              <span className="text-sm font-medium text-[#1A1A1A]">
                {translateInterfaceText("Estratégia de Captura")}</span>
            </div>
            <Switch
              id="is-active"
              checked={isActive}
              onCheckedChange={(checked) =>
                form.setValue('isActive', checked, { shouldDirty: true })
              }
              className="data-[state=checked]:bg-[#3072FF]"
            />
          </div>

          {/* Descrição abaixo do toggle */}
          <p className="text-[13px] text-[#6B7280] leading-relaxed pl-7">
            {translateInterfaceText("Ative ou desative o formulário de captura neste vídeo. Quando ativo, o formulário será exibido no momento configurado.")}</p>
        </div>

        {/* Divisor visual */}
        <div className="h-px bg-[#ECEEF3]" />
      </div>

      {/* Card 2: VÍDEO DA CAPTURA - SEGUNDO - Somente no modo de criação - SEMPRE VISÍVEL */}
      {!isEditMode && (
        <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
          <CardHeader className="pb-4">
            <CardTitle className="text-[17px] font-semibold text-[#101828]">{translateInterfaceText("Vídeo da Captura")}</CardTitle>
            <CardDescription className="text-[13px] text-[#667085]">
              {translateInterfaceText("Escolha o vídeo onde o formulário será exibido")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="video-select" className="text-sm font-medium">
                {translateInterfaceText("Selecionar vídeo *")}</Label>
              {isLoadingVideos ? (
                <div className="flex items-center justify-center h-10 border border-input rounded-md bg-background">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">{translateInterfaceText("Carregando vídeos...")}</span>
                </div>
              ) : (
                <VideoFolderPicker
                  value={videoId}
                  videos={videos}
                  onChange={(value) => form.setValue('videoId', value, { shouldDirty: true })}
                  placeholder={videos.length === 0 ? translateInterfaceText("Nenhum vídeo disponível") : translateInterfaceText("Selecione um vídeo")}
                  disabled={videos.length === 0}
                  triggerClassName="flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm hover:border-[#4B7BFF] transition-colors disabled:opacity-50"
                />
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
